import { readRefused } from "@/lib/api/transportFailure";

/** The first wait before a failed read is sent again; each retry doubles it. */
export const TURN_READ_RETRY_MS = 1_000;
/** The longest wait between two tries of the same read. */
export const TURN_READ_RETRY_CAP_MS = 30_000;

/** How the builder's turn reads end when the page stops caring. */
export function turnCancelledError(): DOMException {
  return new DOMException("The turn was cancelled", "AbortError");
}

function pause(ms: number, abortSignal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const done = () => {
      clearTimeout(timer);
      abortSignal?.removeEventListener("abort", done);
      resolve();
    };
    const timer = setTimeout(done, ms);
    abortSignal?.addEventListener("abort", done);
  });
}

/**
 * A request a turn's watcher needs before it can go on (its own turn, the
 * draft a finished turn left, the turn a reload re-attaches to, the turn's
 * own enqueue). A failed try is not an end: it is sent again after a growing
 * wait until it answers. It ends early only when `ends` says the failure is
 * final (for a read, a typed end no retry changes: an expired session, no
 * access, gone, `readRefused`), or on `abortSignal`.
 */
export async function readUntilAnswered<T>(
  read: () => Promise<T>,
  abortSignal?: AbortSignal,
  ends: (error: unknown) => boolean = readRefused,
): Promise<T> {
  for (let wait = TURN_READ_RETRY_MS; ; ) {
    if (abortSignal?.aborted) throw turnCancelledError();
    try {
      return await read();
    } catch (error) {
      if (abortSignal?.aborted) throw turnCancelledError();
      if (ends(error)) throw error;
    }
    await pause(wait, abortSignal);
    wait = Math.min(wait * 2, TURN_READ_RETRY_CAP_MS);
  }
}
